import type { ContentfulLinkType, ContentfulNavigationType } from '@bedrock/core/contentful';
import { SheetClose } from '@bedrock/ui/sheet';
import { cn } from '@bedrock/ui/utils';
import { ChevronRightIcon } from 'lucide-react';
import MenuButton from './menu-button';
import { NavigationLink } from './navigation-link';
import { getItemHref, getMenuTriggerTitle, isItemActive, isNavigationType } from './utils';

const DRAWER_BUTTON_BASE =
  'drawer-button justify-between active:hover:pl-sm py-md px-sm active:border-none' +
  ' [&>*:first-child]:motion-safe:transition-all' +
  ' [&>*:first-child]:active:border-l-4 [&>*:first-child]:active:pl-xs sm:[&>*:first-child]:active:pl-sm' +
  ' [&>*:first-child]:active:border-foreground' +
  ' [&>*:first-child]:hover:border-l-4 [&>*:first-child]:hover:pl-2xs sm:[&>*:first-child]:hover:pl-xs' +
  ' [&>*:first-child]:hover:border-transparent text-left';

type NavigationItemProps = {
  ref?: React.Ref<HTMLButtonElement | null>;
  item: ContentfulNavigationType | ContentfulLinkType;
  isParentLink: boolean;
  locale: string;
  onEnter: (item: ContentfulNavigationType) => void;
  isInView: boolean;
  pathname: string;
  hash: string;
  parentExcludedPaths?: Set<string>;
};

export default function NavigationItem({
  ref,
  item,
  isParentLink,
  onEnter,
  locale,
  isInView,
  pathname,
  hash,
  parentExcludedPaths,
}: Readonly<NavigationItemProps>) {
  const label = getMenuTriggerTitle(item);

  const isExcludedByParent =
    !isParentLink && !isNavigationType(item) && parentExcludedPaths?.has(getItemHref(item));

  const isActive =
    !isExcludedByParent &&
    isItemActive(
      item,
      pathname,
      hash,
      isParentLink && isNavigationType(item) ? parentExcludedPaths : undefined,
    );
  const activeClass = isActive
    ? '[&>*:first-child]:border-l-4 [&>*:first-child]:pl-xs sm:[&>*:first-child]:pl-sm [&>*:first-child]:border-accent text-accent'
    : '';
  const variantClass = isParentLink
    ? 'br-type-title-xl sm:br-type-title-2xl font-semibold rounded-lg'
    : 'max-sm:p-sm br-type-functional-md-strong sm:br-type-functional-lg-strong';

  return (
    <li className="patterns-navigation-hamburger-item flex w-full">
      <div className="flex-shrink-0 flex-grow-0 basis-full">
        {isNavigationType(item) ? (
          <MenuButton
            ref={ref}
            data-is-parent-link={isParentLink}
            tabIndex={isInView ? 0 : -1}
            className={cn(
              'patterns-navigation-hamburger-action',
              DRAWER_BUTTON_BASE,
              variantClass,
              activeClass,
            )}
            onClick={() => {
              onEnter(item);
            }}
          >
            <span>{label}</span>
            <ChevronRightIcon
              aria-hidden="true"
              className="size-4 min-h-4 min-w-4"
              focusable="false"
            />
          </MenuButton>
        ) : (
          <SheetClose ref={ref} asChild>
            <MenuButton
              asChild
              data-is-parent-link={isParentLink}
              className={cn(
                'patterns-navigation-hamburger-action',
                DRAWER_BUTTON_BASE,
                variantClass,
                activeClass,
              )}
            >
              <NavigationLink link={item} locale={locale} tabIndex={isInView ? 0 : -1}>
                <span>{label}</span>
              </NavigationLink>
            </MenuButton>
          </SheetClose>
        )}
      </div>
    </li>
  );
}
